import { RevealContactsInput_Contacts } from "lib/generated/app-service-gql/graphql";
import { ContactSearchResult } from "lib/hooks/api/contacts/useContactSearch";
import { CognismContactSource, ContactTier } from "lib/types/graphQLEnums";

/**
 * Source-awareness helpers for the unified contacts table (APP-5965). `contactSearch`
 * returns both Cognism and Oscar contacts, badged by `source`; the table renders one
 * row shape but branches reveal/save/PII behaviour on the source.
 */

/** A unified contact row with a stable cross-source selection key attached. */
export type UnifiedContactRow = ContactSearchResult & { selectionKey: string };

/**
 * Globally-unique selection key across sources. Cognism rows key on `redeemId` (the
 * identity reveal/save needs); Oscar rows key on `id` (oscar_contacts.id, the save handle).
 */
export function contactSelectionKey(contact: ContactSearchResult): string {
  const nativeId = contact.source === "oscar" ? contact.id : contact.redeemId ?? contact.id;
  return `${contact.source}:${nativeId}`;
}

/** Attach the selection key to each row so the table can select across sources. */
export function withSelectionKeys(contacts: ContactSearchResult[]): UnifiedContactRow[] {
  return contacts.map((c) => ({ ...c, selectionKey: contactSelectionKey(c) }));
}

/**
 * The reveal state a row renders in — the single source of truth for the details
 * cell, the per-row reveal action, the bulk-reveal selection and the export's credit
 * count. Both sources follow the same rules.
 * - `revealed`: details shown — REDEEMED, including rows the search flipped because the
 *   company had already revealed them.
 * - `unavailable`: the company holds the contact but search-time PII hydration failed
 *   (Cognism unreachable) — show as "unavailable", never as revealable.
 * - `revealable`: a fresh preview — offer Reveal (free here; export charges a credit).
 */
export type ContactRevealState = "revealed" | "unavailable" | "revealable";

export function contactRevealState(contact: ContactSearchResult): ContactRevealState {
  if (contact.tier === ContactTier.Redeemed) return "revealed";
  if (contact.isRevealed) return "unavailable";
  return "revealable";
}

/**
 * The source-tagged ref that reveals or exports a row. Oscar reveals by its node id
 * (oscar_contacts.id); Cognism by its `redeemId`, also carrying the stable contact id as
 * the durable charge/dedup key. A Cognism row without a redeemId has nothing to redeem, so
 * it yields null.
 */
export function contactRevealRef(
  contact: ContactSearchResult,
): RevealContactsInput_Contacts | null {
  if (contact.source === CognismContactSource.Oscar) {
    return { source: contact.source, id: contact.id };
  }
  return contact.redeemId
    ? { source: contact.source, id: contact.redeemId, cognismContactId: contact.id }
    : null;
}

/**
 * The save-to-lead target for a row.
 * - `entityId`: the save/attach handle (Oscar: oscar_contacts.id; Cognism: the stable contact id).
 * - `savedEntityId`: the stored lead `entity_id`, used to resolve "already saved to lead" state.
 *   For Oscar it equals `entityId`; for Cognism it's the `cognism_redeemed_contact` row id, present
 *   only once the contact has been redeemed by this company (`undefined` otherwise, so it's correctly
 *   treated as not-yet-saved).
 */
export type ContactSaveEntity = {
  entityType: "contact" | "contact_cognism";
  entityId: string;
  savedEntityId?: string;
};

/**
 * Save-to-lead target for a row, or null when it can't be saved. Oscar saves by
 * oscar_contacts.id (`contact`); Cognism by its stable contact id (`contact_cognism`,
 * redeem-on-save) — the server resolves a current redeem handle from that id, since the
 * redeemId rotates. A row with no redeemId still isn't saveable: there'd be nothing to resolve.
 */
export function contactSaveEntity(contact: ContactSearchResult): ContactSaveEntity | null {
  if (contact.source === "oscar") {
    return { entityType: "contact", entityId: contact.id, savedEntityId: contact.id };
  }
  return contact.redeemId
    ? {
        entityType: "contact_cognism",
        entityId: contact.id,
        savedEntityId: contact.redeemedContactRowId ?? undefined,
      }
    : null;
}
